import { getAuthManager } from "../systems/auth";

/**
 * No `email` field, by design (EB-617). The API takes the player's identity from the verified
 * Auth0 `sub` on the access token and ignores anything identity-shaped in the body — sending an
 * email here would achieve nothing except putting it back on the wire.
 */
export interface ScoreStats {
  playerName: string | null;
  score: number;
  rank: string;
  ringsCollected: number;
  enemiesStomped: number;
  gameDurationSecs: number;
}

export interface LeaderboardEntry {
  name?: string;
  playerName?: string;
  score: number;
  rank?: string;
}

/** See ScoreStats: identity comes from the token, not from this body (EB-617). */
export interface WeddingScoreStats {
  playerName: string;
  score: number;
  itemsFound?: number;
}

/** Returned by POST /marioymarylin/scores */
export interface WeddingScoreResult {
  /** true = existing record was already better; DB not updated */
  kept: boolean;
  currentBest: number;
}

/**
 * No `email` field, by design (EB-617): GET /marioymarylin/scores is public, and it was returning
 * real addresses to any caller. The row identifies a player by display name only.
 */
export interface WeddingLeaderboardEntry {
  playerName: string;
  score: number;
  itemsFound: number;
  createdAt: string;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || "";

/**
 * Both POST routes sit behind the shared API Gateway authorizer (EB-617), so a submission without
 * a token is a 401 rather than an anonymous write. Returns null when there is no token, which the
 * callers already treat as "not signed in" — both submit paths were gated on a signed-in user
 * before this change, so requiring the token takes no functionality away.
 */
async function authHeaders(): Promise<Record<string, string> | null> {
  const token = await getAuthManager().getAccessToken();
  if (!token) return null;
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}

export async function submitScore(stats: ScoreStats): Promise<void> {
  if (!API_BASE_URL) return;

  try {
    const headers = await authHeaders();
    if (!headers) {
      console.error("Not signed in — score not submitted");
      return;
    }
    await fetch(`${API_BASE_URL}/scores`, {
      method: "POST",
      headers,
      body: JSON.stringify(stats),
    });
  } catch (err) {
    console.error("Failed to submit score:", err);
  }
}

export async function getLeaderboard(): Promise<LeaderboardEntry[]> {
  if (!API_BASE_URL) return [];

  try {
    const res = await fetch(`${API_BASE_URL}/scores`);
    const data = await res.json();
    return data.scores || [];
  } catch (err) {
    console.error("Failed to fetch leaderboard:", err);
    return [];
  }
}

/** Submit score; returns the Lambda result or null on network error. */
export async function submitWeddingScore(
  stats: WeddingScoreStats
): Promise<WeddingScoreResult | null> {
  if (!API_BASE_URL) return null;

  try {
    const headers = await authHeaders();
    if (!headers) {
      console.error("Not signed in — wedding score not submitted");
      return null;
    }
    const res = await fetch(`${API_BASE_URL}/marioymarylin/scores`, {
      method: "POST",
      headers,
      body: JSON.stringify(stats),
    });
    return (await res.json()) as WeddingScoreResult;
  } catch (err) {
    console.error("Failed to submit wedding score:", err);
    return null;
  }
}

/** Fetch top-20 leaderboard sorted by score descending. */
export async function getWeddingLeaderboard(): Promise<WeddingLeaderboardEntry[]> {
  if (!API_BASE_URL) return [];

  try {
    const res  = await fetch(`${API_BASE_URL}/marioymarylin/scores`);
    const data = await res.json();
    return (data.scores ?? []) as WeddingLeaderboardEntry[];
  } catch (err) {
    console.error("Failed to fetch wedding leaderboard:", err);
    return [];
  }
}
